<template>
  <DialogHeader>
    Add folder
    <template #right>
      <div
        v-if="!loading"
        class="font-medium text-green-900"
        @click="handleClick"
      >
        Save
      </div>
      <Icon
        v-else
        name="Loading"
        :size="14"
        class="stroke-gray-800 animate-spin ml-auto"
      />
    </template>
  </DialogHeader>
  <DialogContent>
    <UiInput
      v-model="name"
      label="Folder name"
      :disabled="loading"
      :errorMsg="error"
    />
  </DialogContent>
  <DialogFooter>
    <UiButton
      class="w-1/2 tablet-h:w-auto mr-2.5 tablet-h:mr-2"
      :disabled="loading"
      @click="close()"
    >
      Cancel
    </UiButton>
    <UiButton
      :loading="loading"
      class="w-1/2 tablet-h:w-auto"
      primary
      @click="handleClick"
    >
      Create
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import { UiButton, UiInput, Icon } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { ref, watch } from 'vue'
import { useAddFolderMutation } from '@/shared/graphql/legacy/generated'
import { useRoute, useRouter } from 'vue-router'
import { useExplorerStore } from '@/features/explorer/store'
import { routerApolloClient } from '@/shared/graphql/client'
import gql from 'graphql-tag'

const { close } = useDialog()

const props = defineProps<{
  folderId: string
}>()

const route = useRoute()
const router = useRouter()
const explorerStore = useExplorerStore()

const name = ref('')
const error = ref('')

const { loading, mutate: addFolder } = useAddFolderMutation()

watch(name, () => {
  error.value = ''
})

const handleClick = async () => {
  if (!name.value.trim().length) {
    error.value = 'Please enter a folder name.'
    return
  }

  try {
    const response = await addFolder({
      folderName: name.value,
      parentId: props.folderId || 'root',
    })

    const folder = response?.data?.createFolder

    if (!folder) return

    const currentFolderId = explorerStore.currentFolder?.id || props.folderId

    routerApolloClient.cache.modify({
      id: routerApolloClient.cache.identify({
        __typename: 'Folder',
        id: currentFolderId,
      }),
      fields: {
        items(existing, { readField }) {
          const newFolderRef = routerApolloClient.cache.writeFragment({
            fragment: gql`
              fragment NewFolder on Folder {
                __typename
                id
                name
                path
                createdAt
                totalFiles
              }
            `,
            data: {
              __typename: 'Folder',
              id: folder.id,
              name: folder.name,
              path: `${explorerStore.currentFolder?.path === '/' ? '' : explorerStore.currentFolder?.path}/${folder.name}`,
              createdAt: folder.timestamp,
              totalFiles: 0,
            },
          })

          if (!newFolderRef) return existing

          const newEdge = {
            __typename: 'ItemEdge',
            cursor: folder.id,
            node: newFolderRef,
          }

          return {
            ...existing,
            edges: [newEdge, ...existing.edges],
          }
        },
        totalFiles(existing: number) {
          return existing + 1
        },
      },
    })

    const newFolderPath = `${explorerStore.currentFolder?.path === '/' ? '' : explorerStore.currentFolder?.path}/${folder.name}`

    // Pre-populate the store so the view doesn't reset to null
    explorerStore.setNewFolder({
      id: folder.id,
      name: folder.name,
      path: newFolderPath,
      createdAt: folder.timestamp,
    })

    router.push(`${route.path}/${encodeURIComponent(folder.name)}`)

    close()
  } catch (err) {
    if (err instanceof Error) {
      if (err.message.includes('already')) {
        error.value = 'A folder with this name is already taken'
      } else if (err.message.includes('invalid')) {
        error.value = err.message
      }
    }
  }
}
</script>
